import { useCallback, useEffect, useMemo, useState } from "react";
import { WhatsAppCampaignAPI } from "src/services/WhatsAppCampaignAPI";
import { ApiError } from "src/services/HttpUtils";
import { TEMPLATES_PAGE_SIZE } from "../WhatsAppCampaignConstants";
import type {
  WhatsAppRequestStatus,
  WhatsAppTemplate,
  WhatsAppTemplateStatusFilter,
} from "../WhatsAppCampaignTypes";
import { toHttpUrl, toWhatsAppTemplate } from "../WhatsAppCampaignUtils";

interface TemplatesLoadState {
  status: WhatsAppRequestStatus;
  templates: WhatsAppTemplate[];
  metaUrl: string | null;
  isConnected: boolean;
  errorMessage: string | null;
}

const INITIAL_LOAD_STATE: TemplatesLoadState = {
  status: "loading",
  templates: [],
  metaUrl: null,
  isConnected: true,
  errorMessage: null,
};

const isTemplate = (template: WhatsAppTemplate | null): template is WhatsAppTemplate =>
  template !== null;

export const useWhatsAppTemplates = () => {
  const [loadState, setLoadState] = useState(INITIAL_LOAD_STATE);
  const [reloadCount, setReloadCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<WhatsAppTemplateStatusFilter>("all");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let isStale = false;
    setLoadState(current => ({ ...current, status: "loading" }));

    WhatsAppCampaignAPI.fetchTemplates()
      .then(response => {
        if (isStale) {
          return;
        }
        if (!response.success) {
          setLoadState({
            ...INITIAL_LOAD_STATE,
            status: "error",
            isConnected: response.connected !== false,
            errorMessage: response.message ?? null,
          });
          return;
        }
        const metaUrl = toHttpUrl(response.meta_manage_url);
        setLoadState({
          ...INITIAL_LOAD_STATE,
          status: "success",
          templates: (response.templates ?? [])
            .map(template => toWhatsAppTemplate(template, metaUrl))
            .filter(isTemplate),
          metaUrl,
        });
      })
      .catch((error: unknown) => {
        if (!isStale) {
          setLoadState({
            ...INITIAL_LOAD_STATE,
            status: "error",
            errorMessage: error instanceof ApiError ? error.message : null,
          });
        }
      });

    return () => {
      isStale = true;
    };
  }, [reloadCount]);

  const { templates } = loadState;

  const filteredTemplates = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return templates.filter(
      template =>
        (statusFilter === "all" || template.status === statusFilter) &&
        (!term ||
          template.name.toLowerCase().includes(term) ||
          template.body.toLowerCase().includes(term))
    );
  }, [searchTerm, statusFilter, templates]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: templates.length };
    templates.forEach(template => {
      counts[template.status] = (counts[template.status] ?? 0) + 1;
    });
    return counts;
  }, [templates]);

  const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / TEMPLATES_PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);

  const pageTemplates = useMemo(() => {
    const start = (safePage - 1) * TEMPLATES_PAGE_SIZE;
    return filteredTemplates.slice(start, start + TEMPLATES_PAGE_SIZE);
  }, [filteredTemplates, safePage]);

  const updateSearchTerm = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const updateStatusFilter = useCallback((filter: WhatsAppTemplateStatusFilter) => {
    setStatusFilter(filter);
    setCurrentPage(1);
  }, []);

  const reload = useCallback(() => setReloadCount(count => count + 1), []);
  const goToPreviousPage = useCallback(() => setCurrentPage(Math.max(1, safePage - 1)), [safePage]);
  const goToNextPage = useCallback(
    () => setCurrentPage(Math.min(totalPages, safePage + 1)),
    [safePage, totalPages]
  );

  return {
    templates: pageTemplates,
    loadStatus: loadState.status,
    isConnected: loadState.isConnected,
    errorMessage: loadState.errorMessage,
    metaUrl: loadState.metaUrl,
    hasTemplates: templates.length > 0,
    statusCounts,
    searchTerm,
    statusFilter,
    currentPage: safePage,
    totalPages,
    setSearchTerm: updateSearchTerm,
    setStatusFilter: updateStatusFilter,
    goToPreviousPage,
    goToNextPage,
    reload,
  };
};

export type WhatsAppTemplatesState = ReturnType<typeof useWhatsAppTemplates>;
